import { useEffect } from "react";
import { ArrowRight, Newspaper, FileText } from "lucide-react";
import SiteHeader from "../components/site/SiteHeader";
import SiteFooter from "../components/site/SiteFooter";
import FloatingDonate from "../components/site/FloatingDonate";
import { useT } from "../i18n/LanguageContext";
import {
  IMG_PRESS,
  PRESS_FR,
  PRESS_EN,
  PRESS_WOMEN_OF_SOLUTIONS,
} from "../components/site/siteAssets";

const copy = {
  en: {
    heroKicker: "In the News",
    heroTitle: "The world is listening.",
    heroSub: "Survivor voices, finally heard. From the Las Vegas Review-Journal in 2017 to international magazines in 2026 — every feature is a step toward ending FGM.",

    pressKicker: "As seen in · 2017 — 2026",
    pressTitle: "The world is listening.",
    pressBody: "Four features. One mission. From the Las Vegas Review-Journal in 2017 to international magazines in 2026 — survivor voices, finally heard.",
    pressCta: "Read the original story",
    pressPdf: "Download article (PDF)",
    pressLvLabel: "Las Vegas Review-Journal · 2017",
    pressWomenLabel: "Women of Solutions Magazine · March 2026",
    pressEnLabel: "International feature · 2026",
    pressFrLabel: "Édition francophone · 2026",
  },
  fr: {
    heroKicker: "Dans la presse",
    heroTitle: "Le monde écoute.",
    heroSub: "Les voix des survivantes, enfin entendues. Du Las Vegas Review-Journal en 2017 aux magazines internationaux en 2026 — chaque article est un pas vers la fin des MGF.",

    pressKicker: "Vu dans · 2017 — 2026",
    pressTitle: "Le monde écoute.",
    pressBody: "Quatre reportages. Une seule mission. Du Las Vegas Review-Journal en 2017 aux magazines internationaux en 2026 — les voix des survivantes, enfin entendues.",
    pressCta: "Lire l'article original",
    pressPdf: "Télécharger l'article (PDF)",
    pressLvLabel: "Las Vegas Review-Journal · 2017",
    pressWomenLabel: "Women of Solutions Magazine · Mars 2026",
    pressEnLabel: "Reportage international · 2026",
    pressFrLabel: "Édition francophone · 2026",
  },
};

export default function InTheNews() {
  const t = useT(copy);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F4EDE0] text-[#2A1F15]">
      <SiteHeader />

      {/* Hero */}
      <section className="relative bg-[#1A0F0A] text-[#F4EDE0] py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold" data-testid="in-the-news-hero-kicker">
            <Newspaper size={14} /> {t.heroKicker}
          </div>
          <h1
            className="mt-4 text-5xl md:text-7xl leading-[1.02]"
            style={{ fontFamily: "'Merriweather', serif", fontWeight: 700 }}
            data-testid="in-the-news-hero-title"
          >
            {t.heroTitle}
          </h1>
          <p
            className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[#F4EDE0]/85"
            style={{ fontFamily: "'Merriweather', serif" }}
            data-testid="in-the-news-hero-sub"
          >
            {t.heroSub}
          </p>
        </div>
      </section>

      {/* Press grid */}
      <section className="py-20 md:py-28 bg-white" data-testid="in-the-news-press">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">
              <Newspaper size={14} /> {t.pressKicker}
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl leading-[1.1]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="in-the-news-press-title">{t.pressTitle}</h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-[#2A1F15]/80" style={{ fontFamily: "'Merriweather',serif" }}>{t.pressBody}</p>
          </div>

          <div className="mt-12 grid grid-cols-12 gap-6">
            <article className="col-span-12 md:col-span-6 group relative overflow-hidden rounded-xl border-4 border-[#D4A93B] shadow-2xl bg-black">
              <div className="aspect-[4/5]">
                <img src={PRESS_WOMEN_OF_SOLUTIONS} alt="Women of Solutions Magazine Special Women's Month Edition March 2026" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 bg-gradient-to-t from-black/95 via-black/70 to-transparent text-white">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4A93B] font-bold">{t.pressWomenLabel}</div>
              </div>
            </article>

            <div className="col-span-12 md:col-span-6 grid grid-cols-1 gap-6">
              <article className="group relative overflow-hidden rounded-xl border-4 border-[#2A1F15] shadow-xl bg-white">
                <div className="aspect-[16/10]">
                  <img src={PRESS_EN} alt="A survivor who became a global voice against female genital mutilation" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold">{t.pressEnLabel}</div>
                </div>
              </article>

              <article className="group relative overflow-hidden rounded-xl border-4 border-[#00A651] shadow-xl bg-white">
                <div className="aspect-[16/10]">
                  <img src={PRESS_FR} alt="Sélifa Boukari McGreevy — Briser le silence, sauver des vies" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#8C5A3C] font-bold">{t.pressFrLabel}</div>
                </div>
              </article>
            </div>
          </div>

          <div
            data-testid="in-the-news-press-card-lv"
            className="mt-8 grid grid-cols-12 gap-6 items-center bg-[#F4EDE0] rounded-xl border-2 border-[#2A1F15]/10 hover:border-[#E3282B] hover:shadow-xl transition-all p-5 md:p-6 overflow-hidden"
          >
            <div className="col-span-12 md:col-span-3">
              <div className="aspect-[4/5] overflow-hidden rounded-lg">
                <img src={IMG_PRESS} alt="Las Vegas Review-Journal feature, 2017" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#E3282B] font-bold">{t.pressLvLabel}</div>
              <h3 className="mt-2 text-2xl md:text-3xl leading-tight" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>&ldquo;Las Vegas woman fights to stop female genital mutilation.&rdquo;</h3>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <a
                  href="https://www.reviewjournal.com/local/local-las-vegas/las-vegas-woman-fights-to-stop-female-genital-mutilation/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="in-the-news-press-cta"
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#E3282B] text-white text-sm font-bold hover:bg-[#8C5A3C] transition-colors"
                >
                  {t.pressCta} <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href="https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/1ujf6dpr_F_Las-Vegas-woman-fights-to-stop-female-genital-mutilation-_-Las-1%20%281%29.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="in-the-news-press-pdf"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#2A1F15] text-[#2A1F15] text-sm font-bold hover:bg-[#2A1F15] hover:text-white transition-colors"
                >
                  <FileText size={14} /> {t.pressPdf}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <FloatingDonate />
    </div>
  );
}
